import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Loader } from '@ge/components/loader';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import { Placeholders, DayJs } from '@ge/models/constants';

import { ReportWidgetTable } from '../../tables/report-widget-table';

import { inspectionDamagesColumnsDefs, InspectionDamagesColumns } from './inspection-damages-cols';

dayjs.extend(isoWeek);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const InspectionDamagesTableWidget = ({
  name,
  columns,
  data = [],
  isEditing,
  isInteractive,
  onRowSelectionChange,
}) => {
  const { t, ready } = useTranslation(['reporting.widgets']);
  // initialize all rows being selected
  const [rowsSelected] = useState(() => data.map(({ id }) => id));

  useEffect(() => {
    onRowSelectionChange(rowsSelected);
  }, [onRowSelectionChange, rowsSelected]);

  const cellValueMapFn = useCallback(
    (cellValue) => {
      let indDate = null;
      if (cellValue.indicationDate) {
        const date = dayjs(cellValue.indicationDate).format('DD MMM YY');
        indDate = date !== DayJs.INVALID_DATE && date;
      }
      return {
        [InspectionDamagesColumns.SITE]: cellValue.site ? cellValue.site : Placeholders.DASH,
        [InspectionDamagesColumns.ASSET]: cellValue.asset ? cellValue.asset : Placeholders.DASH,
        [InspectionDamagesColumns.MODEL]: cellValue.model ? cellValue.model : Placeholders.DASH,
        [InspectionDamagesColumns.COMPONENT]: cellValue.component
          ? cellValue.component
          : Placeholders.DASH,
        [InspectionDamagesColumns.LOCATION]: cellValue.location
          ? cellValue.location
          : Placeholders.DASH,
        [InspectionDamagesColumns.DAMAGE]: cellValue.damage ? cellValue.damage : Placeholders.DASH,
        [InspectionDamagesColumns.PART]: cellValue.part ? cellValue.part : Placeholders.DASH,
        [InspectionDamagesColumns.CATEGORY]: cellValue.category
          ? cellValue.category
          : Placeholders.DASH,
        [InspectionDamagesColumns.MANUFACTURER]: cellValue.manufacturer
          ? cellValue.manufacturer
          : Placeholders.DASH,
        [InspectionDamagesColumns.INDICATION_START_DATE]: indDate ? indDate : Placeholders.DASH,
        [InspectionDamagesColumns.P90]: cellValue.p90 ? cellValue.p90 : Placeholders.DASH,
      };
    },
    [t],
  );

  // Table factories
  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: inspectionDamagesColumnsDefs,
    cellValueMapFn,
  });

  if (!ready) {
    return <Loader />;
  }

  return (
    <Container>
      <ReportWidgetTable
        key={name}
        compressed
        noTitles
        scrollable={isInteractive}
        transparent
        columnGroupFactory={columnGroupFactory}
        columnFactory={columnFactory}
        cellFactory={cellFactory}
        columns={columns}
        values={data}
        onValueSelect={() => null}
        rowsSelected={rowsSelected}
        rowKeyProperty={'id'}
        isEditing={isEditing}
        variableHeight={true}
        vAlign={'middle'}
      />
    </Container>
  );
};

InspectionDamagesTableWidget.propTypes = {
  name: PropTypes.string.isRequired,
  data: PropTypes.instanceOf(Object),
  columns: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  isEditing: PropTypes.bool,
  isInteractive: PropTypes.bool,
  onRowSelectionChange: PropTypes.func.isRequired,
};
